import { useRef, useState } from 'react'
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Spinner,
    HStack,
    Alert,
    AlertIcon,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    useDisclosure
} from '@chakra-ui/react'
import ReactSelect from 'react-select'
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi'
import { ResourceConfig, AGE_GROUPS } from './schema'
import { useAdminList, useAdminMutations, useReferenceOptions } from './hooks'
import AdminResourceForm from './AdminResourceForm'

export default function AdminResourcePage({ config }: { config: ResourceConfig }) {
    const [ageGroup, setAgeGroup] = useState('')
    const [parentId, setParentId] = useState('')

    const params: Record<string, string | number | undefined> = {}
    if (config.ageGroupFilter && ageGroup) params.ageGroup = ageGroup
    if (config.parentFilter && parentId) params[config.parentFilter.param] = parentId

    const { items, loading } = useAdminList(config.resource, params)
    const { create, update, remove } = useAdminMutations(config.resource, config.singular)

    const formDisclosure = useDisclosure()
    const [editing, setEditing] = useState<Record<string, unknown> | null>(null)

    const deleteDisclosure = useDisclosure()
    const [deleting, setDeleting] = useState<Record<string, unknown> | null>(null)
    const cancelRef = useRef<HTMLButtonElement>(null)

    const parentOptions = useReferenceOptions(
        config.parentFilter?.refResource,
        config.parentFilter?.refLabel ?? ((i) => String(i._id))
    )

    const openCreate = () => {
        setEditing(null)
        formDisclosure.onOpen()
    }
    const openEdit = (item: Record<string, unknown>) => {
        setEditing(item)
        formDisclosure.onOpen()
    }

    const handleSubmit = async (payload: Record<string, unknown>) => {
        if (editing) {
            await update.mutateAsync({ id: String(editing._id), body: payload })
        } else {
            await create.mutateAsync(payload)
        }
    }

    const confirmDelete = async () => {
        if (deleting) await remove.mutateAsync(String(deleting._id))
        deleteDisclosure.onClose()
        setDeleting(null)
    }

    return (
        <Box>
            <Flex justify="space-between" align="start" mb={2} flexWrap="wrap" gap={3}>
                <Box>
                    <Heading size="lg">{config.label}</Heading>
                    <Text color="gray.600" fontSize="sm" maxW="2xl">
                        {config.description}
                    </Text>
                </Box>
                <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={openCreate}>
                    New {config.singular}
                </Button>
            </Flex>

            <Alert
                status="info"
                borderRadius="md"
                mt={3}
                fontSize="sm"
                alignItems="start"
            >
                <AlertIcon />
                <Box>
                    <Text fontWeight="semibold">Where this shows up</Text>
                    <Text color="gray.700">{config.appContext}</Text>
                </Box>
            </Alert>

            <HStack my={4} spacing={4} flexWrap="wrap">
                {config.ageGroupFilter && (
                    <Box minW="240px">
                        <ReactSelect
                            isClearable
                            placeholder="All age groups"
                            options={AGE_GROUPS.map((g) => ({ value: g, label: g }))}
                            value={ageGroup ? { value: ageGroup, label: ageGroup } : null}
                            onChange={(opt) =>
                                setAgeGroup(
                                    (opt as { value: string } | null)?.value ?? ''
                                )
                            }
                        />
                    </Box>
                )}
                {config.parentFilter && (
                    <Box minW="280px">
                        <ReactSelect
                            isClearable
                            placeholder={`Filter by ${config.parentFilter.label.toLowerCase()}`}
                            options={parentOptions.options}
                            value={
                                parentOptions.options.find((o) => o.value === parentId) ??
                                null
                            }
                            onChange={(opt) =>
                                setParentId(
                                    (opt as { value: string } | null)?.value ?? ''
                                )
                            }
                        />
                    </Box>
                )}
            </HStack>

            <Box bg="white" borderRadius="md" borderWidth="1px" overflowX="auto">
                {loading ? (
                    <Flex justify="center" p={10}>
                        <Spinner size="lg" />
                    </Flex>
                ) : items.length === 0 ? (
                    <Text p={8} color="gray.500" textAlign="center">
                        No {config.label.toLowerCase()} yet. Click “New {config.singular}”
                        to add one.
                    </Text>
                ) : (
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                {config.columns.map((c) => (
                                    <Th key={c.key}>{c.label}</Th>
                                ))}
                                <Th textAlign="right">Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {items.map((item) => (
                                <Tr key={item._id}>
                                    {config.columns.map((c) => (
                                        <Td key={c.key}>
                                            {c.render
                                                ? c.render(item)
                                                : String(item[c.key] ?? '')}
                                        </Td>
                                    ))}
                                    <Td>
                                        <HStack justify="flex-end" spacing={1}>
                                            <IconButton
                                                aria-label="Edit"
                                                icon={<FiEdit2 />}
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => openEdit(item)}
                                            />
                                            <IconButton
                                                aria-label="Delete"
                                                icon={<FiTrash2 />}
                                                size="sm"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() => {
                                                    setDeleting(item)
                                                    deleteDisclosure.onOpen()
                                                }}
                                            />
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </Box>

            <AdminResourceForm
                config={config}
                record={editing}
                isOpen={formDisclosure.isOpen}
                onClose={formDisclosure.onClose}
                onSubmit={handleSubmit}
                submitting={create.isPending || update.isPending}
            />

            <AlertDialog
                isOpen={deleteDisclosure.isOpen}
                leastDestructiveRef={cancelRef}
                onClose={deleteDisclosure.onClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader>Delete {config.singular}</AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter gap={3}>
                            <Button ref={cancelRef} onClick={deleteDisclosure.onClose}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="red"
                                onClick={confirmDelete}
                                isLoading={remove.isPending}
                            >
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    )
}
